import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import './PricingPage.css';

const PricingPage = () => {
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [processing, setProcessing] = useState(false);
  const [userEmail, setUserEmail] = useState('');
  const [showLoginPrompt, setShowLoginPrompt] = useState(false);
  const [loginPromptPlan, setLoginPromptPlan] = useState(null);
  const [expandedFaq, setExpandedFaq] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();

  const faqData = [
    {
      question: "What features are included in all plans?",
      answer: "All plans include unlimited job posts, unlimited candidates, all assessment features, interview scheduling, offer letter management, full analytics dashboard, priority email support, mobile app access, and all future updates."
    },
    {
      question: "Can I change my plan later?",
      answer: "Yes, you can upgrade or downgrade your plan at any time. When upgrading, you'll be charged the prorated difference. When downgrading, the change will take effect at the end of your current billing cycle."
    },
    {
      question: "What payment methods do you accept?",
      answer: "We accept all major credit cards, debit cards, UPI, net banking, and digital wallets. All payments are processed securely through our payment gateway."
    },
    {
      question: "Is there a refund policy?",
      answer: "Yes, we offer a 14-day money-back guarantee on all plans. If you're not satisfied with our service, contact us within 14 days of purchase for a full refund."
    },
    {
      question: "Do you offer discounts for longer commitments?",
      answer: "Yes! Our 6-month plan saves you ₹1,000 (17% discount) and our yearly plan saves you ₹2,000 (17% discount) compared to monthly billing."
    },
    {
      question: "What happens after my trial ends?",
      answer: "After your trial ends, you'll need to select a paid plan to continue accessing the platform. All your data will be preserved and available immediately after you subscribe."
    },
    {
      question: "Can I cancel my subscription anytime?",
      answer: "Yes, you can cancel your subscription at any time. You'll continue to have access until the end of your current billing period. No questions asked."
    },
    {
      question: "Do you provide invoices?",
      answer: "Yes, we automatically generate and email invoices for all payments. You can also download invoices from your account dashboard at any time."
    }
  ];

  useEffect(() => {
    const getTrialEndedEmail = () => {
      if (location.state?.userEmail) {
        return location.state.userEmail;
      }
      
      const trialEndedEmail = sessionStorage.getItem('trialEndedEmail');
      if (trialEndedEmail) {
        return trialEndedEmail;
      }
      
      const sessionEmail = sessionStorage.getItem('userEmail');
      if (sessionEmail) {
        return sessionEmail;
      }
      
      const userData = sessionStorage.getItem('userData');
      if (userData) {
        try {
          const parsedData = JSON.parse(userData);
          if (parsedData.email) {
            return parsedData.email;
          }
        } catch (error) {
          console.error('Error parsing user data:', error);
        }
      }
      
      return '';
    };

    const email = getTrialEndedEmail();
    if (email) {
      setUserEmail(email);
      sessionStorage.setItem('userEmail', email);
    }

    loadPlans();
  }, [navigate, location]);

  const loadPlans = async () => {
    try {
      const rechargePlans = [
        {
          id: 'monthly',
          name: 'Monthly Plan',
          price: 1000,
          period: 'month',
          originalPrice: 1000,
          savings: 0,
          popular: false,
          description: 'Pay monthly, cancel anytime',
          monthlyEquivalent: 1000
        },
        {
          id: 'sixmonths',
          name: '6-Month Plan',
          price: 5000,
          period: '6 months',
          originalPrice: 6000,
          savings: 1000,
          popular: true,
          description: 'Most Popular - Save ₹1,000',
          monthlyEquivalent: 833
        },
        {
          id: 'yearly',
          name: 'Yearly Plan',
          price: 10000,
          period: 'year',
          originalPrice: 12000,
          savings: 2000,
          popular: false,
          description: 'Best Value - Save ₹2,000',
          monthlyEquivalent: 833
        }
      ];

      setPlans(rechargePlans);
    } catch (error) {
      console.error('Error loading plans:', error);
    }
  };

  const handleUpgrade = async (plan) => {
    setSelectedPlan(plan);
    setProcessing(true);

    try {
      const token = sessionStorage.getItem('authToken');
      const currentUserEmail = sessionStorage.getItem('userEmail') || userEmail;

      if (!token) {
        setLoginPromptPlan(plan);
        setShowLoginPrompt(true);
        setProcessing(false);
        return;
      }

      console.log('🚀 Redirecting to payment page with plan:', plan);
      navigate('/payment', { state: { plan } });
      
    } catch (error) {
      console.error('Payment initiation error:', error);
      alert('Failed to initiate payment. Please try again.');
      setProcessing(false);
    } finally {
      setProcessing(false);
    }
  };

  const handleBackToLogin = () => {
    navigate('/access-recruit');
  };

  const handleContinueWithoutLogin = () => {
    setShowLoginPrompt(false);
    setLoginPromptPlan(null);
  };

  const handleLoginAndUpgrade = () => {
    const actualUserEmail = userEmail || 'user@company.com';
    
    const tempUser = {
      email: actualUserEmail,
      name: actualUserEmail.split('@')[0] || 'User',
      subscription: {
        plan: 'trial_ended',
        paymentStatus: 'pending'
      }
    };
    
    sessionStorage.setItem('authToken', 'temp_token_' + Date.now());
    sessionStorage.setItem('userData', JSON.stringify(tempUser));
    sessionStorage.setItem('userEmail', actualUserEmail);
    
    console.log('🚀 Creating guest session for:', actualUserEmail);
    console.log('📦 Redirecting to payment with plan:', loginPromptPlan);
    
    navigate('/payment', { state: { plan: loginPromptPlan } });
  };

  const toggleFaq = (index) => {
    setExpandedFaq(expandedFaq === index ? null : index);
  };

  const commonFeatures = [
    'Unlimited Job Posts',
    'Unlimited Candidates',
    'All Assessment Features',
    'Interview Scheduling',
    'Offer Letter Management',
    'Priority Email Support',
  ];

  if (showLoginPrompt && loginPromptPlan) {
    return (
      <div className="pricoh-container">
        <div className="pricoh-login-prompt">
          <div className="pricoh-prompt-content">
            <div className="pricoh-prompt-header">
              <h2 className="pricoh-prompt-title">Sign In Required</h2>
              <p className="pricoh-prompt-subtitle">Please sign in to activate your selected plan and get full platform access.</p>
            </div>
            
            <div className="pricoh-selected-plan-info">
              <div className="pricoh-plan-badge-prompt">{loginPromptPlan.name}</div>
              <div className="pricoh-plan-price-prompt">
                <span className="pricoh-currency">₹</span>
                <span className="pricoh-amount">{loginPromptPlan.price.toLocaleString()}</span>
                <span className="pricoh-period">/{loginPromptPlan.period}</span>
              </div>
              {loginPromptPlan.savings > 0 && (
                <div className="pricoh-savings-badge-prompt">
                  Save ₹{loginPromptPlan.savings.toLocaleString()}
                </div>
              )}
            </div>

            {userEmail && (
              <div className="pricoh-user-email-notice">
                <strong>Account:</strong> {userEmail}
              </div>
            )}

            <div className="pricoh-prompt-buttons">
              <button 
                onClick={handleLoginAndUpgrade}
                className="pricoh-btn-primary"
              >
                Continue to Payment
              </button>
              <button 
                onClick={handleContinueWithoutLogin}
                className="pricoh-btn-secondary"
              >
                View Other Plans
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="pricoh-container">
      {/* Header */}
      <div className="pricoh-header">
        <div className="pricoh-header-content">
          <h1 className="pricoh-main-title">Choose Your Plan</h1>
          <p className="pricoh-header-subtitle">All plans include complete access to all features - choose your preferred billing period</p>
          {userEmail ? (
            <div className="pricoh-user-email-badge">
              <span className="pricoh-email-label">Account:</span>
              <span className="pricoh-email-value">{userEmail}</span>
            </div>
          ) : (
            <div className="pricoh-guest-notice">
              <span className="pricoh-guest-icon">👋</span>
              Viewing as guest - sign in to purchase
            </div>
          )}
        </div>
      </div>

      {/* Trial Ended Warning */}
      {userEmail && (
        <div className="pricoh-trial-ended-banner">
          <div className="pricoh-trial-ended-content">
            {/* <div className="pricoh-warning-icon">⚠️</div> */}
            <div className="pricoh-warning-text">
              <h3 className="pricoh-warning-title">Your Free Trial Has Ended</h3>
              <p className="pricoh-warning-desc">Choose a plan to continue accessing all recruitment features and tools.</p>
              <div className="pricoh-trial-account-info">
                Account: <strong>{userEmail}</strong>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Pricing Plans */}
      <div className="pricoh-pricing-grid">
        {plans.map((plan) => (
          <div
            key={plan.id}
            className={`pricoh-pricing-card ${plan.popular ? "pricoh-pricing-card--popular" : ""}`}
          >
            {plan.popular && (
              <div className="pricoh-pricing-card-badge">
                <span className="pricoh-badge-text">MOST POPULAR</span>
              </div>
            )}

            <div className="pricoh-card-header">
              <h3 className="pricoh-plan-name">{plan.name}</h3>
              <p className="pricoh-plan-description">{plan.description}</p>

              <div className="pricoh-price-section">
                <div className="pricoh-price-main">
                  <span className="pricoh-price-currency">₹</span>
                  <span className="pricoh-price-amount">
                    {plan.price.toLocaleString()}
                  </span>
                </div>

                <div className="pricoh-price-period">per {plan.period}</div>

                {plan.id !== "monthly" && (
                  <div className="pricoh-monthly-equivalent">
                    ₹{plan.monthlyEquivalent}/month
                  </div>
                )}

                {plan.savings > 0 && (
                  <div className="pricoh-savings-badge">
                    Save ₹{plan.savings.toLocaleString()}
                  </div>
                )}
              </div>
            </div>

            <div className="pricoh-card-footer">
              <button
                className={`pricoh-upgrade-button ${
                  plan.popular ? "pricoh-upgrade-button--highlight" : ""
                }`}
                onClick={() => handleUpgrade(plan)}
                disabled={processing}
              >
                {processing && selectedPlan?.id === plan.id ? (
                  <>
                    <div className="pricoh-button-spinner"></div>
                    Processing...
                  </>
                ) : (
                  `Get Started`
                )}
              </button>

              {!userEmail && <p className="pricoh-login-warning">Sign in required to purchase</p>}
            </div>
          </div>
        ))}
      </div>

      {/* Features Section */}
      <div className="pricoh-features-section">
        <div className="pricoh-features-header">
          <h2 className="pricoh-features-title">Everything You Need, In Every Plan</h2>
          <p className="pricoh-features-subtitle">All plans include the complete feature set - no hidden tiers or restrictions</p>
        </div>
        <div className="pricoh-features-grid">
          {commonFeatures.map((feature, index) => (
            <div key={index} className="pricoh-feature-item">
              <span className="pricoh-feature-icon">✓</span>
              <span className="pricoh-feature-text">{feature}</span>
            </div>
          ))}
        </div>
      </div>

      {/* FAQ Section */}
      <div className="pricoh-faq-section">
        <div className="pricoh-faq-header">
          <h2 className="pricoh-faq-title">Frequently Asked Questions</h2>
          <p className="pricoh-faq-subtitle">Find answers to common questions about our pricing and plans</p>
        </div>
        <div className="pricoh-faq-list">
          {faqData.map((faq, index) => (
            <div key={index} className={`pricoh-faq-item ${expandedFaq === index ? 'pricoh-faq-item--active' : ''}`}>
              <div className="pricoh-faq-question" onClick={() => toggleFaq(index)}>
                <h3 className="pricoh-question-text">{faq.question}</h3>
                <span className={`pricoh-faq-icon ${expandedFaq === index ? 'pricoh-faq-icon--open' : ''}`}>
                  {expandedFaq === index ? '−' : '+'}
                </span>
              </div>
              <div className={`pricoh-faq-answer ${expandedFaq === index ? 'pricoh-faq-answer--open' : ''}`}>
                <p className="pricoh-answer-text">{faq.answer}</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Support Section */}
      <div className="pricoh-support-section">
        <div className="pricoh-support-content">
          <h3 className="pricoh-support-title">Still Have Questions?</h3>
          <p className="pricoh-support-subtitle">Our team is here to help you choose the right plan for your needs</p>
          <div className="pricoh-contact-options">
            <a href="mailto:support@recruit.com" className="pricoh-contact-link">
              <span className="pricoh-contact-icon">📧</span>
              support@humanex.com
            </a>
            <a href="tel:+919876543210" className="pricoh-contact-link">
              <span className="pricoh-contact-icon">📞</span>
              +91 7305717915
            </a>
          </div>
        </div>
      </div>

      {/* Back to Login */}
      <div className="pricoh-back-to-login">
        <button onClick={handleBackToLogin} className="pricoh-back-button">
          <span className="pricoh-back-arrow">←</span>
          Back to Login
        </button>
      </div>
    </div>
  );
};

export default PricingPage;